import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import StatusPill from '@/components/ui/StatusPill';
import type { ProdHistory } from '@/hooks/useDashboard';
import { productionTypeMeta } from '@/lib/labels';
import { useLang } from '@/lib/lang';
import { relativeTime } from '@/lib/time';

/** One finished run in the history list. */
export default function ProductionRow({ production }: { production: ProdHistory }) {
  const navigate = useNavigate();
  const { t } = useTranslation(['dashboard', 'common']);
  const lang = useLang();
  const meta = productionTypeMeta(t, production.config?.production_type || production.production_type);
  const genre = production.config?.genre_override || '';

  return (
    <button
      type="button"
      onClick={() => navigate(`/projects/${production.project_id}/productions/${production.id}`)}
      className={
        'flex w-full items-center justify-between gap-4 rounded-xl border border-border bg-card px-5 py-3.5 ' +
        'text-left transition-all hover:-translate-y-px hover:border-primary/40'
      }
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h4 className="truncate font-display text-[14px] font-semibold">
            {production.project_title || production.script_preview || t('empty.untitled')}
          </h4>
          <StatusPill tone="neutral">{meta.label}</StatusPill>
          {genre && <StatusPill tone="neutral">{genre.replace(/_/g, ' ')}</StatusPill>}
        </div>
        <p className="mt-1 font-mono text-[11px] text-faint">
          {production.id} ·{' '}
          {relativeTime(production.updated_at || production.completed_at || production.started_at || production.created_at, lang)}
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-3">
        <StatusPill status={production.status}>{production.status || 'unknown'}</StatusPill>
        <ArrowRight className="h-4 w-4 text-faint" />
      </div>
    </button>
  );
}
